import { useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import useSmartState from '../../../../common/hooks/useSmartState';
import { SmartContext } from '../../../../library/Core/SmartContext';
import { getDomainValueForCode } from '../../../../library/Core/SmartFunctions';
import { FormControl, FormSection } from '../../../../library/Core/SmartTypes';
import ActionSettings from '../../../../library/SimpleControls/ActionSettings';
import InternalAdminQRReportService from './InternalAdminQRReportService';
import { REPORT_CONFIG } from './report-config';
import useInternalAdminReports from './../useInternalAdminReports';
import { confirmDeleteAlumniInstitute } from '../../../../library/Service/confirmDeleteAlumniInstitute';
import ViewQRUpdationModal from './ViewQRUpdationModal';

const BATCH_SIZE = 10;

const Results = () => {
    const { state, dispatch } = useContext(SmartContext);
    const allRowsData = state.data.alumniList ?? [];
    const { setStateValue } = useSmartState();
    const { getFormattedDate, deleteUser } = useInternalAdminReports();
    const resultsFormConfig = state.formConfig?.sectionRepository[1] as FormSection;
    const config = REPORT_CONFIG[state.routeInfo.pageName];
    const loadMoreRef = useRef(null);
    const [pageData, setPageData] = useState([] as any[]);

    useEffect(() => {
        setPageData([...allRowsData.slice(0, BATCH_SIZE)]);
    }, [state.data.alumniList]);

    useEffect(() => {
        const loadMoreObserver = new IntersectionObserver((entries) => {
            if (allRowsData.length <= pageData.length) {
                if (loadMoreRef.current) loadMoreObserver.unobserve(loadMoreRef.current);
                return;
            }

            if (entries[0].isIntersecting) {
                const nextBatch = allRowsData.slice(0, pageData.length + BATCH_SIZE);
                setPageData([...nextBatch]);
            }
        });

        if (loadMoreRef.current) loadMoreObserver.observe(loadMoreRef.current);

        return () => {
            if (loadMoreRef.current) {
                loadMoreObserver.unobserve(loadMoreRef.current);
            }
        };
    }, [loadMoreRef, pageData]);

    async function handleOpenModal(id = '') {
        dispatch({
            type: 'CONTROL_VALUE_CHANGE',
            payload: {
                dataKey: 'selectedId',
                value: parseInt(id),
            },
        });

        dispatch({ type: 'TOGGLE_MODAL_VISIBILITY', payload: { modalName: 'showQRUpdationModal' } });
    };

    const getAlumniDetailsField = (row: any) => {
        return (
            <div className="d-flex flex-column checkoption">
                <p className="tablink" >
                    <span
                        data-bs-toggle="tooltip"
                        title={`${row?.name}`}
                        className="singlelinename font-16 lineHeight-16">
                        {row?.name}
                        <span className="d-block font-13 fst-italic"> {row?.programName} - {row?.passoutYear} <br/> {row?.emailId} <br/> {row?.contact}</span>
                    </span>
                </p>
            </div>
        );
    };

    const getShortenedSchoolName = (row: any): any => {

        if (!row?.schoolName || typeof row.schoolName !== 'string') {
            // Gracefully handle missing/invalid schoolName
            return <span style={{ color: 'gray' }}>N/A</span>;
        }
        const parts = row?.schoolName.split(',');
        const schoolNameContent =
            parts.length > 1 ? (
                <>
                    <span>
                        {parts[0]},<br />
                        {parts.slice(1).join(',').trim()}
                    </span>
                </>
            ) : (
                <>{row.schoolName}</>
            );

        if (state.routeInfo.pageName === 'internal-admin-all-school-summary') {
            return (
                <a className="tablink" href={`/support/all-alumni-report/${row.schoolId}`} target="_blank" rel="noopener noreferrer">
                    {schoolNameContent}
                </a>
            );
        }

         if (state.routeInfo.pageName === 'internal-admin-all-school-statistics') {
            return (
                    <a
                        className="tablink d-flex align-items-center gap-2"
                        href={`/support/school-statistics/${row.schoolId}`}
                        target="_blank"
                        rel="noopener noreferrer">
                        <b className="me-2 position-relative ratio-90 d-flex align-items-center justify-content-center">
                            <img src={row.logo} className="img-fluid object-fit-cover" width={90}  />
                        </b>
                        <span
                            data-bs-toggle="tooltip"
                            title={row.schoolName}
                            className="font-16 lineHeight-16">{schoolNameContent}
                        </span></a>
                );
        }
        // If condition is not satisfied, display just the school name text
        return schoolNameContent;
    };

    const getPurpose = (row: any): any => {
            return (
                    <p
                        className="tablink d-flex align-items-center gap-2">
                        <span>
                            {`${getDomainValueForCode(row?.category, 'QR_CATEGORY',state)}`} - {`${getDomainValueForCode(row?.mode, 'QR_MODE',state)}`}
                        </span>
                    </p>
                );
    };

    const getActionNode = (control: FormControl, row: any) => {
        const actions = config['ROW_ACTION'].filter((action: any) => {
            let flag = true;
            if (row.isPeerReviewed == 1 && action === 'VALIDATE') flag = false;
            if (row.isEmailVerified === 1 && action === 'VERIFY_EMAIL') flag = false;
            // if (row.userStatusCode === 'ACTIVE' && action === 'ACTIVATE') return false;
            if (row.userStatusCode !== 'INACTIVE' && action === 'ACTIVATE') flag = false;
            if (row.userStatusCode === 'INACTIVE' && action === 'DEACTIVATE') flag = false;
            if(action == 'UPDATE') flag = false;
            if(row.userStatusCode == 'RAW_PROFILE') flag = action == 'UPDATE' ? true : false;
            if(row.isEngagementDataPresent != 1 && action == 'ENGAGEMENT_API_RUN' ) flag= false
            return flag;
        });

        return actions.length > 1 ? (
            <ActionSettings id={row} actions={actions} onClick={handleAction} />
        ) : (
            <a className="btn-update" href={''} target="_blank" rel="noopener noreferrer">
                Update
            </a>
        );
    };

    const handleAction = (uuid: string, action: string) => {
        console.log(uuid);
        handlePerformAction(action, [uuid]);
    };

    const getFieldNode = (control: FormControl, row: any) => {
        let content;

        switch (control.id) {
            case 'alumniDetails':
                content = getAlumniDetailsField(row);
                break;
            case 'schoolName':
                content = getShortenedSchoolName(row);
                break;
            case 'purpose':
                content = getPurpose(row);
                break;
            case 'activityDate':
            case 'addedOnDate':
                content = getFormattedDate(row[control.id]);
                break;
            case 'openModal':
                content = (
                    <>
                        <a
                        href="javascript:void(0)"
                        className="link" 
                        onClick={() => handleOpenModal(row?.id)}
                        ><span>Update</span></a>
                    </>
                );
                break;
            default:
                if (control.props?.domainCategoryCode?.length > 0)
                    content = getDomainValueForCode(row[control.id], control.props.domainCategoryCode, state);
                else content = row[control.id];
        }

        return (
            <td key={control.id} className={control.className}>
                {content}
            </td>
        );
    };

    const handlePerformAction = async (action: string, students: any) => {
        switch (action) {
            case 'DELETE': {
                const selection = await confirmDeleteAlumniInstitute(
                    students?.[0]?.uuid,
                    students?.[0]?.schoolId ?? students?.[0]?.instituteId ?? students?.[0]?.institute_id
                );
                if (!selection) break;
                await deleteUser([{ uuid: selection.uuid, instituteId: selection.instituteId }]);
                InternalAdminQRReportService.search(state.data.searchCriteria, state.routeInfo.pageName).then((response: any) => {
                    setStateValue('alumniList', response.data);
                    fetchAlumniCount(state);
                    toast.success('Deleted successfully');
                });
                break;
            }
        }
    };

    const fetchAlumniCount = async (state: any) => {
        try {
            const alumniCountData = await InternalAdminQRReportService.searchCount(state.data.searchCriteria, state.routeInfo.pageName);

            dispatch({
                type: 'SET_INTERNAL_STATE',
                payload: { key: 'alumniCount', value: alumniCountData?.data },
            });
        } catch (error) {
            console.error('Error fetching alumni count:', error);
        }
    };

    return (
        <div>
            <div className="ActiveListings_present">
                <div className="d-flex flex-wrap align-items-center mb-3 w-100 justify-content-between mailerLinks-parent">
                    <div className="d-flex flex-wrap align-items-center flex-1 mailerLinks my-2"></div>
                    <div className="d-flex flex-wrap align-items-center justify-content-end">
                        <h3 className="font-14 font-500 mb-0 me-2">
                            Total {state?.internal?.alumniCount && state?.internal?.alumniCount[0]?.totalAlumni} records
                        </h3>
                    </div>
                </div>
                <div className="table-responsive attendee_dtl">
                    <table className={`table ${config.TABLE_CLASS}`} style={config.TABLE_STYLE}>
                        <thead>
                            <tr>
                                {resultsFormConfig.controlGroup.map((field: FormControl, index: number) => (
                                    <th
                                        key={field.props.label}
                                        style={config?.TABLE_WIDTH?.length > index ? { width: config?.TABLE_WIDTH[index] } : {}}>
                                        {field.props.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pageData &&
                                pageData.map((row: any) => (
                                    <tr key={row?.name+''+row?.institute_id+''+row?.email_id} className="">
                                        {resultsFormConfig.controlGroup.map((field: FormControl) => getFieldNode(field, row))}
                                    </tr>
                                ))}
                            <tr key="loadMoreRef" ref={loadMoreRef} />
                        </tbody>
                    </table>
                </div>
            </div>
            <ViewQRUpdationModal />
        </div>
    );
};

export default Results;
